/* eslint-disable camelcase */

export const SOURCE_CONTEXTS = [
  'grade',
  'numeric',
  'datetime',
  'comment',
  'selection',
  'selection_multi',
  'photos',
  'signature',
  'variable'
]

export const STATE_LABELS = {
  eq: 'est égale à',
  neq: 'différent de',
  gt: 'supérieur à',
  gte: 'supérieur ou égal à',
  lt: 'inférieur à',
  lte: 'inférieur ou égal à',
  inc: 'contient',
  ninc: 'ne contient pas',
  filled: 'est rempli',
  empty: 'est vide',
  in: 'fait partie de',
  nin: 'ne fait pas partie de'
}

const STATES_BY_CONTEXT = {
  grade: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty', 'in', 'nin'],
  numeric: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty', 'in', 'nin'],
  datetime: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty', 'in', 'nin'],
  comment: ['eq', 'neq', 'inc', 'ninc', 'filled', 'empty'],
  selection: ['eq', 'neq', 'filled', 'empty', 'in', 'nin'],
  selection_multi: ['inc', 'ninc', 'filled', 'empty', 'in', 'nin'],
  photos: ['filled', 'empty'],
  signature: ['filled', 'empty'],
  variable: ['eq', 'neq', 'filled', 'empty', 'in', 'nin']
}

const DEFAULT_STATES = ['in', 'nin']

export const KIND_OPTIONS = [
  { value: 'show', label: 'Afficher' },
  { value: 'hide', label: 'Masquer' }
]

let sequence = 0
export const uid = prefix => `${prefix}-${Date.now()}-${sequence++}`

export const isPersisted = record => Boolean(record.id)

export const flattenQuestions = sections =>
  (sections || []).flatMap(section => section.questions || [])

export const questionLabel = question =>
  question.title && question.title.trim() ? question.title : '(Question sans titre)'

export const findQuestion = (sections, questionId) =>
  flattenQuestions(sections).find(question => String(question.id) === String(questionId))

const findVariable = (variables, variableId) =>
  (variables || []).find(variable => String(variable.id) === String(variableId))

const scopedSections = (sections, sectionId) =>
  sectionId ? (sections || []).filter(section => String(section.id) === String(sectionId)) : sections || []

export const questionSectionId = (sections, questionId) => {
  const section = (sections || [])
    .find(item => (item.questions || []).some(question => String(question.id) === String(questionId)))
  return section ? section.id : null
}

export const ruleSectionId = (sections, records, excludeUniqueId) => {
  const ref = (records || []).find(record =>
    record.uniqueId !== excludeUniqueId && !record.shouldDestroy
    && record.targetable_type === 'Question'
    && questionSectionId(sections, record.targetable_id) !== null)
  return ref ? questionSectionId(sections, ref.targetable_id) : null
}

const sectionGroupName = section => section.name || '(Section sans nom)'

const questionSourceOption = question => ({
  value: `Question:${question.id}`,
  label: questionLabel(question),
  type: 'Question',
  sourceId: String(question.id),
  context: question.context
})

export const flattenGroupOptions = groups =>
  (groups || []).flatMap(group => group.options ? group.options : [group])

export const sourceGroups = (sections, sectionId) =>
  scopedSections(sections, sectionId)
    .map(section => ({
      key: String(section.id),
      label: sectionGroupName(section),
      options: (section.questions || [])
        .filter(question => SOURCE_CONTEXTS.includes(question.context))
        .map(questionSourceOption)
    }))
    .filter(group => group.options.length > 0)

export const variableSourceOptions = variables =>
  (variables || [])
    .filter(variable => variable.variable_type === 'text' && (variable.values || []).length > 0)
    .map(variable => ({
      value: `Variable:${variable.id}`,
      label: variable.name,
      type: 'Variable',
      sourceId: String(variable.id),
      context: 'variable'
    }))

export const variableAnswerOptions = (variables, variableId) => {
  const variable = (variables || []).find(item => String(item.id) === String(variableId))
  return (variable?.values || []).map(value => ({ value, label: value }))
}

export const sourceValue = (groups, type, id) =>
  flattenGroupOptions(groups).find(option => option.value === `${type}:${id}`) || null

export const targetValue = (groups, id) =>
  flattenGroupOptions(groups).find(option => option.value === String(id)) || null

const filterGroups = (groups, keep) =>
  groups
    .map(group => ({ ...group, options: group.options.filter(keep) }))
    .filter(group => group.options.length > 0)

export const excludeSourceQuestions = (groups, questionIds) =>
  filterGroups(groups, option => option.type !== 'Question' || !questionIds.has(option.sourceId))

export const excludeTargetQuestions = (groups, questionIds) =>
  filterGroups(groups, option => !questionIds.has(option.value))

const questionPosition = (sections, questionId) => findQuestion(sections, questionId)?.position

// Mirrors Rule#display_rule_question_order, which rejects sources.max > targets.min.
export const excludeQuestionsBeforeSources = (groups, sections, sourceQuestionIds) => {
  const positions = Array.from(sourceQuestionIds)
    .map(id => questionPosition(sections, id))
    .filter(position => Number.isInteger(position))
  if (positions.length === 0)
    return groups

  const lastSource = Math.max(...positions)
  return filterGroups(groups, option => {
    const position = questionPosition(sections, option.value)
    return !Number.isInteger(position) || position >= lastSource
  })
}

export const sourceContext = (sections, variables, target) => {
  if (target.targetable_type === 'Variable')
    return findVariable(variables, target.targetable_id) ? 'variable' : null
  const question = findQuestion(sections, target.targetable_id)
  return question ? question.context : null
}

export const sourceLabel = (sections, variables, target) => {
  if (target.targetable_type === 'Variable') {
    const variable = findVariable(variables, target.targetable_id)
    return variable ? variable.name : ''
  }
  const question = findQuestion(sections, target.targetable_id)
  return question ? questionLabel(question) : ''
}

export const targetQuestionOptions = (sections, sectionId) =>
  scopedSections(sections, sectionId)
    .map(section => ({
      key: String(section.id),
      label: sectionGroupName(section),
      options: (section.questions || [])
        .map(question => ({ value: String(question.id), label: questionLabel(question) }))
    }))
    .filter(group => group.options.length > 0)

export const answerOptions = (sections, questionId) => {
  const question = findQuestion(sections, questionId)
  return (question?.options || []).map(option => ({ value: String(option.id), label: option.content }))
}

export const gradeOptions = (question, gradeConfig) => {
  if (!question || !gradeConfig)
    return []
  const { gradeMin, gradeSize } = gradeConfig
  return (question.options || [])
    .filter(option => option.position < gradeSize)
    .map(option => ({ value: String(option.position + gradeMin), label: option.content }))
}

export const valueOptions = (sections, condition, gradeConfig) => {
  const question = findQuestion(sections, condition.targetable_id)
  if (question && question.context === 'grade')
    return gradeOptions(question, gradeConfig)
  return answerOptions(sections, condition.targetable_id)
}

export const stateOptionsFor = context =>
  (STATES_BY_CONTEXT[context] || DEFAULT_STATES).map(value => ({ value, label: STATE_LABELS[value] }))

const datetimeKind = format => {
  if (format === 'only_date')
    return 'date'
  if (format === 'only_time')
    return 'time'
  return 'datetime'
}

export const valueInputKind = (context, state, datetimeFormat) => {
  if (state === 'filled' || state === 'empty')
    return 'none'
  if (context === 'grade')
    return state === 'in' || state === 'nin' ? 'option-multi' : 'option'
  if (context === 'selection' || context === 'selection_multi')
    return state === 'eq' || state === 'neq' ? 'option' : 'option-multi'
  if (state === 'in' || state === 'nin')
    return 'tags'
  if (context === 'numeric')
    return 'number'
  if (context === 'datetime')
    return datetimeKind(datetimeFormat)
  return 'text'
}

export const sourceDatetimeFormat = (sections, target) => {
  if (target.targetable_type !== 'Question')
    return null
  const question = findQuestion(sections, target.targetable_id)
  return question ? question.datetime_format : null
}

export const splitValue = value =>
  value ? String(value).split(';').filter(Boolean) : []

export const joinValue = ids => ids.length ? ids.join(';') : null

export const selectValue = (options, current) =>
  options.find(option => option.value === String(current)) || null

export const stateLabel = state => STATE_LABELS[state] || state

export const kindLabel = kind => {
  const match = KIND_OPTIONS.find(option => option.value === kind)
  return match ? match.label : kind
}

export const optionLabels = (sections, questionId, value) => {
  const byValue = new Map(answerOptions(sections, questionId).map(option => [option.value, option.label]))
  return splitValue(value).map(id => byValue.get(id) || id)
}

const gradeValueLabels = (question, gradeConfig, value) => {
  const byValue = new Map(gradeOptions(question, gradeConfig).map(option => [option.value, option.label]))
  return splitValue(value).map(grade => byValue.get(grade) || grade).join(', ')
}

export const conditionValueLabel = (refs, condition) => {
  const { sections, variables, gradeConfig } = refs
  const context = sourceContext(sections, variables, condition)
  const kind = valueInputKind(context, condition.state, sourceDatetimeFormat(sections, condition))
  if (kind === 'none')
    return ''
  if (kind === 'option' || kind === 'option-multi') {
    if (context === 'grade')
      return gradeValueLabels(findQuestion(sections, condition.targetable_id), gradeConfig, condition.value)
    return optionLabels(sections, condition.targetable_id, condition.value).join(', ')
  }
  if (kind === 'tags')
    return splitValue(condition.value).join(', ')
  return condition.value || ''
}

export const findQuestionLabel = (sections, questionId) => {
  const question = findQuestion(sections, questionId)
  return question ? questionLabel(question) : ''
}

export const activeRules = rules => (rules || []).filter(rule => !rule.shouldDestroy)

const NO_SECTION_LABEL = '(Sans section)'

const numberEntries = list => list.map((rule, index) => ({ rule, number: index + 1 }))

export const groupBySections = (sections, rules) => {
  const bySectionId = new Map()
  const activeList = rules || []
  activeList.forEach(rule => {
    const key = ruleSectionId(sections, [...rule.conditions, ...rule.rule_actions], null)
    const mapKey = key === null ? 'none' : String(key)
    if (!bySectionId.has(mapKey))
      bySectionId.set(mapKey, [])
    bySectionId.get(mapKey).push(rule)
  })

  const groups = (sections || [])
    .map(section => ({
      sectionId: section.id,
      name: section.name || '(Section sans nom)',
      entries: numberEntries(bySectionId.get(String(section.id)) || [])
    }))

  const orphanRules = bySectionId.get('none') || []
  if (orphanRules.length > 0)
    groups.push({ sectionId: null, name: NO_SECTION_LABEL, entries: numberEntries(orphanRules) })

  return groups
}

const referencesQuestion = (records, questionId) => {
  const id = String(questionId)
  return (records || []).some(record =>
    !record.shouldDestroy && record.targetable_type === 'Question'
    && String(record.targetable_id) === id)
}

export const ruleNumberIndexFromGroups = groups => {
  const index = new Map()
  groups.forEach(group =>
    group.entries.forEach(entry => index.set(entry.rule.uniqueId, entry.number)))
  return index
}

export const linkedRuleNumbers = (numberIndex, rules, questionId) =>
  activeRules(rules)
    .filter(rule => referencesQuestion(rule.conditions, questionId)
      || referencesQuestion(rule.rule_actions, questionId))
    .map(rule => numberIndex.get(rule.uniqueId))
    .filter(Boolean)
    .sort((first, second) => first - second)

export const formatNumberList = numbers => {
  if (numbers.length <= 1)
    return numbers.join('')
  return `${numbers.slice(0, -1).join(', ')} et ${numbers[numbers.length - 1]}`
}

export const emptyCondition = () => ({
  uniqueId: uid('condition'),
  targetable_type: 'Question',
  targetable_id: null,
  state: 'in',
  value: null
})

export const emptyAction = () => ({
  uniqueId: uid('action'),
  kind: 'show',
  targetable_type: 'Question',
  targetable_id: null
})

export const emptyRule = () => ({
  uniqueId: uid('rule'),
  operator: 'every',
  conditions: [emptyCondition()],
  rule_actions: [emptyAction()]
})

export const ruleForQuestionSource = (sections, questionId) => {
  const rule = emptyRule()
  const question = findQuestion(sections, questionId)
  const [firstState] = stateOptionsFor(question ? question.context : null)
  rule.conditions[0] = {
    ...rule.conditions[0],
    targetable_type: 'Question',
    targetable_id: String(questionId),
    state: firstState ? firstState.value : rule.conditions[0].state
  }
  return rule
}

export const normalizeRules = rules => (rules || []).map(rule => ({
  id: rule.id,
  uniqueId: uid('rule'),
  operator: rule.operator || 'every',
  conditions: (rule.conditions || []).map(condition => ({
    id: condition.id,
    uniqueId: uid('condition'),
    targetable_type: condition.targetable_type || 'Question',
    targetable_id: condition.targetable_id,
    state: condition.state || 'in',
    value: condition.value
  })),
  rule_actions: (rule.rule_actions || []).map(action => ({
    id: action.id,
    uniqueId: uid('action'),
    kind: action.kind,
    targetable_type: action.targetable_type || 'Question',
    targetable_id: action.targetable_id
  }))
}))

export const duplicateRule = rule => ({
  uniqueId: uid('rule'),
  operator: rule.operator,
  conditions: rule.conditions.map(condition => ({
    uniqueId: uid('condition'),
    targetable_type: condition.targetable_type,
    targetable_id: condition.targetable_id,
    state: condition.state,
    value: condition.value
  })),
  rule_actions: rule.rule_actions.map(action => ({
    uniqueId: uid('action'),
    kind: action.kind,
    targetable_type: action.targetable_type,
    targetable_id: action.targetable_id
  }))
})

export const pruneRulesForQuestions = (rules, questionIds) => {
  const ids = new Set((questionIds || []).map(String))
  const hits = record =>
    record.targetable_type === 'Question' && ids.has(String(record.targetable_id))
  const prune = records => (records || [])
    .map(record => hits(record) && isPersisted(record) ? { ...record, shouldDestroy: true } : record)
    .filter(record => !(hits(record) && !isPersisted(record)))
  const hasActive = records => (records || []).some(record => !record.shouldDestroy)

  return (rules || [])
    .map(rule => {
      if (rule.shouldDestroy)
        return rule
      const conditions = prune(rule.conditions)
      const rule_actions = prune(rule.rule_actions)
      if (!hasActive(conditions) || !hasActive(rule_actions))
        return isPersisted(rule) ? { ...rule, shouldDestroy: true } : null
      return { ...rule, conditions, rule_actions }
    })
    .filter(Boolean)
}

const serializeNested = (records, fields) =>
  (records || [])
    .filter(record => !(record.shouldDestroy && !isPersisted(record)))
    .map(record => {
      if (record.shouldDestroy)
        return { id: record.id, _destroy: true }

      const result = isPersisted(record) ? { id: record.id } : {}
      fields.forEach(field => { result[field] = record[field] })
      return result
    })

export const submittedRules = rules =>
  (rules || []).filter(rule => !(rule.shouldDestroy && !isPersisted(rule)))

export const serializeRules = rules =>
  submittedRules(rules)
    .map(rule => {
      if (rule.shouldDestroy)
        return { id: rule.id, _destroy: true }

      const result = isPersisted(rule) ? { id: rule.id } : {}
      result.operator = rule.operator
      result.conditions_attributes = serializeNested(
        rule.conditions, ['targetable_type', 'targetable_id', 'state', 'value']
      )
      result.rule_actions_attributes = serializeNested(
        rule.rule_actions, ['kind', 'targetable_type', 'targetable_id']
      )
      return result
    })

const RULE_ERROR_KEY = /^rules\[(?<index>\d+)\]$/u
const QUESTION_ERROR_KEY = /^questions\[(?<id>\d+)\]$/u

export const flatMessages = value => {
  if (typeof value === 'string')
    return [value]
  if (Array.isArray(value))
    return value.flatMap(flatMessages)

  return Object.values(value || {}).flatMap(flatMessages)
}

const actionQuestionIds = rule => (rule.rule_actions || [])
  .filter(action => !action.shouldDestroy && action.targetable_type === 'Question'
    && action.targetable_id)
  .map(action => String(action.targetable_id))

const scopedMessagesByQuestion = value => Object.entries(value || {})
  .map(([key, messages]) => [key.match(QUESTION_ERROR_KEY), messages])
  .filter(([match]) => Boolean(match))
  .map(([match, messages]) => [match.groups.id, flatMessages(messages)])

const messagesByQuestion = (rule, value) => {
  const scoped = scopedMessagesByQuestion(value)
  if (scoped.length > 0)
    return scoped

  const messages = flatMessages(value)
  return actionQuestionIds(rule).map(questionId => [questionId, messages])
}

const errorPlacements = (errors, refs) => {
  const submitted = submittedRules(refs.rules)
  return Object.keys(errors).flatMap(key => {
    const match = key.match(RULE_ERROR_KEY)
    const rule = match ? submitted[Number(match.groups.index)] : null
    if (!rule)
      return []

    const number = refs.numberIndex.get(rule.uniqueId) || null
    return messagesByQuestion(rule, errors[key])
      .map(([questionId, messages]) => ({
        questionId,
        sectionId: questionSectionId(refs.sections, questionId),
        number,
        messages,
        sourceKey: key
      }))
      .filter(placement => placement.sectionId !== null)
  })
}

const groupByQuestion = placements => {
  const grouped = new Map()
  placements.forEach(placement => {
    const mapKey = `${placement.sectionId}|${placement.questionId}`
    const entry = grouped.get(mapKey)
      || { sectionId: placement.sectionId, questionId: placement.questionId, numbers: [], messages: [] }
    if (placement.number && !entry.numbers.includes(placement.number))
      entry.numbers.push(placement.number)
    placement.messages.forEach(message => {
      if (!entry.messages.includes(message))
        entry.messages.push(message)
    })
    grouped.set(mapKey, entry)
  })
  grouped.forEach(entry => entry.numbers.sort((first, second) => first - second))
  return [...grouped.values()]
}

export const mergeRuleErrors = (errors, refs) => {
  const placements = errorPlacements(errors || {}, refs)
  if (placements.length === 0)
    return errors || {}

  const placed = new Set(placements.map(placement => placement.sourceKey))
  const merged = Object.fromEntries(Object.entries(errors).filter(([key]) => !placed.has(key)))
  groupByQuestion(placements).forEach(({ sectionId, questionId, numbers, messages }) => {
    const sectionKey = `sections[${sectionId}]`
    const section = { ...merged[sectionKey] }
    const questionKey = `questions[${questionId}]`
    section[questionKey] = { ...section[questionKey], conditional: { numbers, messages } }
    merged[sectionKey] = section
  })
  return merged
}
